const BASE = process.env.REACT_APP_KNAME_ASSET_BASE;
const V = 'v1';

export const CARD_ASSET_VERSION = V;

export const assetUrl = {
  background: (id) => `${BASE}/${CARD_ASSET_VERSION}/backgrounds/bg_${id}.png`,
  character: (id) => `${BASE}/${CARD_ASSET_VERSION}/characters/${id}.svg`,
  plate: () => `${BASE}/${CARD_ASSET_VERSION}/plate/name-plate.svg`, // 앞뒤 공용 명패
  backBase: () => `${BASE}/${CARD_ASSET_VERSION}/plate/card-back.png`, // 뒷장 베이스
  font: () => `${BASE}/${CARD_ASSET_VERSION}/fonts/HeirofLightOTFBold.otf`,
};

// ── 이미지 로더 (CORS + 캐시) ─────────────────────
const imgCache = new Map();

export const loadImage = (url) => {
  if (imgCache.has(url)) return imgCache.get(url);
  const promise = new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`asset load failed: ${url}`));
    img.src = url;
  });
  imgCache.set(url, promise);
  return promise;
};

/** 이미 캐시에 올라와 있는지 (진행률 초기값 계산용) */
export const isImageCached = (url) => imgCache.has(url);

// ── 폰트 로더 (1회) ───────────────────────────────
export const KNAME_FONT = 'KnameFont';
let fontPromise = null;

export const ensureFont = () => {
  if (!fontPromise) {
    fontPromise = new FontFace(KNAME_FONT, `url(${assetUrl.font()})`)
      .load()
      .then((face) => {
        document.fonts.add(face);
      });
  }
  return fontPromise;
};

export const isFontRequested = () => fontPromise !== null;

// ── 사전 로딩 ─────────────────────────────────────

/** 이 selection 을 그리는 데 필요한 에셋 URL 목록 */
const assetJobsFor = ({ zodiacId, backgroundId } = {}) => {
  const jobs = [
    { key: 'font', run: ensureFont },
    { key: 'plate', run: () => loadImage(assetUrl.plate()) },
    { key: 'backBase', run: () => loadImage(assetUrl.backBase()) },
  ];
  if (backgroundId) {
    jobs.push({
      key: 'background',
      run: () => loadImage(assetUrl.background(backgroundId)),
    });
  }
  if (zodiacId) {
    jobs.push({
      key: 'character',
      run: () => loadImage(assetUrl.character(zodiacId)),
    });
  }
  return jobs;
};

/** 진행률 총량 계산용 — 실제 로드 없이 개수만 센다. */
export const countCardAssets = (selection) => assetJobsFor(selection).length;

/**
 * 카드 렌더에 필요한 에셋을 미리 내려받아 캐시를 데운다.
 *
 * @param {{zodiacId?:string, backgroundId?:string}} selection
 * @param {(key:string)=>void} [onStep] 에셋 하나가 끝날 때마다 호출 (성공·실패 모두)
 *
 * 실패해도 렌더 시점에 다시 시도하므로 allSettled 로 조용히 넘긴다.
 */
export const preloadCardAssets = (selection = {}, onStep) => {
  const tick = (key) => {
    try {
      onStep?.(key);
    } catch (e) {
      // 진행률 콜백 오류가 로딩을 막지 않도록 한다.
    }
  };
  return Promise.allSettled(
    assetJobsFor(selection).map(({ key, run }) =>
      run().then(
        (v) => {
          tick(key);
          return v;
        },
        (e) => {
          tick(key);
          throw e;
        },
      ),
    ),
  );
};

// ── 캐릭터 알파 바운딩 박스 (캐릭터당 1회 계산 후 캐시) ──
const bboxCache = new Map();

export const rasterize = (img, w = 1200, h = 1800) => {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  c.getContext('2d').drawImage(img, 0, 0, w, h);
  return c;
};

export const getAlphaBbox = (source, cacheKey) => {
  if (bboxCache.has(cacheKey)) return bboxCache.get(cacheKey);
  const c =
    source instanceof HTMLCanvasElement
      ? source
      : (() => {
          const t = document.createElement('canvas');
          t.width = source.naturalWidth;
          t.height = source.naturalHeight;
          t.getContext('2d', { willReadFrequently: true }).drawImage(
            source,
            0,
            0,
          );
          return t;
        })();
  const ctx = c.getContext('2d', { willReadFrequently: true });
  const { data, width, height } = ctx.getImageData(0, 0, c.width, c.height);

  let minX = width,
    minY = height,
    maxX = -1,
    maxY = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (data[(y * width + x) * 4 + 3] > 0) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  const bbox = { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
  bboxCache.set(cacheKey, bbox);
  return bbox;
};

/** 원본 비율 유지 래스터화 (개별 크기 SVG용) */
export const toNaturalCanvas = (img) => {
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  if (!w || !h) {
    throw new Error(
      'SVG에 width/height 또는 viewBox가 없어 크기를 알 수 없음 — 파일 수정 필요',
    );
  }
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  c.getContext('2d').drawImage(img, 0, 0, w, h);
  return c;
};
